import React, { useState, useEffect } from 'react';
import { salesPaymentsReceivedApi, type PaymentReceivedDto } from '../../../services/api';
import RecordPaymentModal from './RecordPaymentModal';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank Transfer',
  check: 'Check',
  card: 'Card',
  other: 'Other',
};

export default function PaymentsReceivedPage() {
  const [payments, setPayments] = useState<PaymentReceivedDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const loadPayments = () => {
    setLoading(true);
    salesPaymentsReceivedApi
      .list()
      .then((res) => {
        if (res.data) setPayments(res.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPayments();
  }, []);

  const isEmpty = !loading && payments.length === 0;

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments Received</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track customer payments and receipts</p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 shadow-sm shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Record Payment
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-card flex-1 min-h-0 overflow-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <svg
              className="w-10 h-10 animate-spin text-brand-500 mb-3"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-sm">Loading payments...</p>
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Payment Tracking</h2>
            <p className="text-sm text-gray-500 max-w-md">
              Record payments received from customers against their outstanding invoices. Track
              partial payments, bank deposits, and payment methods.
            </p>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Record Payment
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="text-left text-xs font-semibold text-gray-600 py-3 px-4">Date</th>
                  <th className="text-left text-xs font-semibold text-gray-600 py-3 px-4">Customer</th>
                  <th className="text-left text-xs font-semibold text-gray-600 py-3 px-4">Invoice</th>
                  <th className="text-left text-xs font-semibold text-gray-600 py-3 px-4">Method</th>
                  <th className="text-right text-xs font-semibold text-gray-600 py-3 px-4">Amount</th>
                  <th className="text-left text-xs font-semibold text-gray-600 py-3 px-4">Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50/50">
                    <td className="py-3 px-4 text-sm text-gray-900">{p.payment_date}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{p.customer_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {p.invoice_number ?? '—'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {PAYMENT_METHOD_LABELS[p.payment_method] ?? p.payment_method}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900 text-right">
                      {formatCurrency(p.amount)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">{p.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RecordPaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={loadPayments}
      />
    </div>
  );
}
